import { openProductQuickView } from "@/components/productQuickView/store"
import { Badge } from "@/components/ui/Badge"
import { Button } from "@/components/ui/Button"
import { flagForCurrency } from "@/components/ui/Flag"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/Table"
import { shortDate, units } from "@/lib/format"
import { BADGE_VARIANT } from "@/pages/Inventory/components/StockStatus"
import type { Market, NeedMarket, NeedProduct } from "../lib/types"

interface Props {
  products: NeedProduct[]
  markets: Market[]
  today: string
}

type MarketsById = Map<string, Market>

const HEAD = "h-8 px-2 py-0 text-xs whitespace-nowrap"
const NUMBER = "whitespace-nowrap text-right tabular-nums"
const DASH = <span className="text-muted-foreground">–</span>
// Columns before the market's own numbers: product, supplier, lead time, MOQ.
const PRODUCT_COLUMNS = 4
// The market's own numbers: market, stock, incoming, sales a day, runs out,
// order by, days left.
const MARKET_COLUMNS = 7

function addDays(iso: string, n: number): string {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10)
}

// 487, 15, 7.7, 0.24: whole units once there are 10 or more a day.
function perDay(value: number | null) {
  if (value === null) return DASH
  return value >= 10 ? units(Math.round(value)) : value.toLocaleString("en-US", { maximumSignificantDigits: 2 })
}

// The quick view opens on `market`: the row's market for a product needed in
// one market; null (the panel's own choice) on the all-markets row.
function ProductCells({ product, market }: { product: NeedProduct; market: Market | null }) {
  const suppliers = product.suppliers.join(", ")
  const name = product.name ?? product.product_external_id
  return (
    <>
      <TableCell className="w-full max-w-0">
        <Button
          variant="link"
          className="h-auto min-w-0 max-w-full justify-start gap-2 p-0 text-sm font-normal"
          title={[name, product.sku].filter(Boolean).join(" · ")}
          onClick={() => openProductQuickView(product.product_external_id, market)}
        >
          <span className="shrink-0 tabular-nums">{product.product_external_id}</span>
          <span className="truncate">{name}</span>
        </Button>
      </TableCell>
      <TableCell className="max-w-40 truncate" title={suppliers || undefined}>
        {suppliers || <Badge variant="warning">No supplier</Badge>}
      </TableCell>
      <TableCell className={NUMBER}>
        {product.lead_time_days}
        {product.lead_time_default && "*"}
      </TableCell>
      <TableCell className={NUMBER}>{product.moq === null ? DASH : units(product.moq)}</TableCell>
    </>
  )
}

function MarketCells({ line, market, today }: { line: NeedMarket; market: Market | undefined; today: string }) {
  const Flag = flagForCurrency(market?.currency)
  return (
    <>
      <TableCell className="whitespace-nowrap">
        <div className="flex items-center gap-2">
          {Flag && <Flag />}
          {market?.name ?? "Unknown market"}
        </div>
      </TableCell>
      <TableCell className={NUMBER}>{units(line.on_hand)}</TableCell>
      <TableCell className={NUMBER}>{line.incoming > 0 ? units(line.incoming) : DASH}</TableCell>
      <TableCell className={NUMBER}>{perDay(line.daily_sales)}</TableCell>
      <TableCell className={NUMBER}>{shortDate(addDays(today, line.stockout_days), today)}</TableCell>
      <TableCell className={NUMBER}>{shortDate(addDays(today, line.buffer_days), today)}</TableCell>
      <TableCell className={NUMBER}>
        <Badge variant={BADGE_VARIANT[line.status]} className="tabular-nums">{line.buffer_days.toLocaleString("en-US")}</Badge>
      </TableCell>
    </>
  )
}

function Quantity({ value, raised, needed }: { value: number | null; raised?: boolean; needed?: number }) {
  if (value === null) return <span title="Sales are unknown for part of the period the order must cover.">Unknown</span>
  return (
    <span className="font-semibold" title={raised && needed !== undefined ? `Needs ${units(needed)}, raised to the minimum order` : undefined}>
      {units(value)}
      {raised && " ↑"}
    </span>
  )
}

const PMS = <TableCell className="text-center">{DASH}</TableCell>

// One row for a product needed in one market. A product needed in several
// markets gets a row with the order for all of them, then a row per market.
function ProductRows({ product, markets, today }: { product: NeedProduct; markets: MarketsById; today: string }) {
  const [only] = product.markets
  if (product.markets.length === 1) {
    const market = markets.get(only.market_id)
    return (
      <TableRow>
        <ProductCells product={product} market={market ?? null} />
        <MarketCells line={only} market={market} today={today} />
        <TableCell className={NUMBER}>
          <Quantity value={product.quantity} raised={product.raised_to_moq} needed={only.quantity ?? undefined} />
        </TableCell>
        {PMS}
      </TableRow>
    )
  }
  return (
    <>
      <TableRow className="border-b-0">
        <ProductCells product={product} market={null} />
        <TableCell colSpan={MARKET_COLUMNS} />
        <TableCell className={NUMBER}><Quantity value={product.quantity} raised={product.raised_to_moq} /></TableCell>
        {PMS}
      </TableRow>
      {product.markets.map((line, index) => (
        <TableRow key={line.market_id} className={index < product.markets.length - 1 ? "border-b-0" : undefined}>
          <TableCell colSpan={PRODUCT_COLUMNS} />
          <MarketCells line={line} market={markets.get(line.market_id)} today={today} />
          <TableCell className={NUMBER}><Quantity value={line.quantity} /></TableCell>
          <TableCell />
        </TableRow>
      ))}
    </>
  )
}

// The Purchase Plan as one sheet: a row per product, most overdue first.
// Days left is the days between the last day to order and today; negative
// means the order is already late.
export default function NeedTable({ products, markets, today }: Props) {
  const byId: MarketsById = new Map(markets.map((m) => [m.id, m]))
  return (
    <>
      <Table className="[&_td]:px-2 [&_td]:py-1">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className={HEAD}>Product</TableHead>
            <TableHead className={HEAD}>Supplier</TableHead>
            <TableHead className={`${HEAD} text-right`}>Lead time</TableHead>
            <TableHead className={`${HEAD} text-right`}>MOQ</TableHead>
            <TableHead className={HEAD}>Market</TableHead>
            <TableHead className={`${HEAD} text-right`}>Stock</TableHead>
            <TableHead className={`${HEAD} text-right`}>Incoming</TableHead>
            <TableHead className={`${HEAD} text-right`}>Sales / day</TableHead>
            <TableHead className={`${HEAD} text-right`}>Runs out</TableHead>
            <TableHead className={`${HEAD} text-right`}>Order by</TableHead>
            <TableHead className={`${HEAD} text-right`}>Days left</TableHead>
            <TableHead className={`${HEAD} text-right`}>Order</TableHead>
            <TableHead className={`${HEAD} text-center`}>PMS</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product) => (
            <ProductRows key={product.product_external_id} product={product} markets={byId} today={today} />
          ))}
        </TableBody>
      </Table>
      <div className="flex flex-wrap gap-x-6 border-t px-2 py-2 text-xs">
        <span>* No supplier lead time on record: 75-day default</span>
        <span>↑ Raised to the supplier's minimum order</span>
        <span>Days left below 0: the order is late</span>
      </div>
    </>
  )
}
